import { FC, useState } from 'react';
import { Modal, Input, Typography } from 'antd';

import FlexCol from '../atoms/FlexCol';

const { Text } = Typography;

/** Phrase a user must type to confirm archiving an entity and all its records. */
const CONFIRM_PHRASE = 'DELETE RECORDS';

export interface DeleteStructureConfirmProps {
    open: boolean;
    /** 'form' archives a form type (STRONG); 'entity' archives a record type and all records (VERY STRONG). */
    kind: 'form' | 'entity';
    /** Name the user must type to confirm. */
    artifactName: string;
    loading?: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

/**
 * Escalating typed-confirmation modal for archiving (soft-deleting) a form type
 * or an entity together with its subjacent documents. A form type requires
 * typing its name (STRONG); an entity additionally requires a destructive
 * phrase (VERY STRONG) because archiving cascades to every record. The confirm
 * button stays disabled — never hidden — until the required tokens match.
 */
const DeleteStructureConfirm: FC<DeleteStructureConfirmProps> = ({
    open,
    kind,
    artifactName,
    loading,
    onCancel,
    onConfirm,
}) => {
    const [nameInput, setNameInput] = useState('');
    const [phraseInput, setPhraseInput] = useState('');

    const requiresPhrase = kind === 'entity';
    const nameMatches = nameInput.trim() === artifactName;
    const phraseMatches = !requiresPhrase || phraseInput.trim() === CONFIRM_PHRASE;
    const canConfirm = !!artifactName && nameMatches && phraseMatches;

    const reset = () => {
        setNameInput('');
        setPhraseInput('');
    };

    const handleCancel = () => {
        reset();
        onCancel();
    };

    const handleConfirm = () => {
        reset();
        onConfirm();
    };

    const noun = kind === 'form' ? 'form type' : 'record type';
    const childNoun = kind === 'form' ? 'layouts' : 'records';

    return (
        <Modal
            title={`Delete ${kind === 'form' ? 'Form' : 'Record'} Type`}
            open={open}
            onOk={handleConfirm}
            onCancel={handleCancel}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true, loading, disabled: !canConfirm }}
        >
            <FlexCol gap={10}>
                <Text>
                    This archives the {noun} <Text strong>{artifactName}</Text> and all of its {childNoun}. It can be restored from the archive.
                </Text>
                <Text type={requiresPhrase ? 'danger' : 'warning'}>
                    {requiresPhrase
                        ? `This affects every record of this type. Type the name, then "${CONFIRM_PHRASE}", to proceed.`
                        : 'Type the name to proceed.'}
                </Text>
                <Input
                    aria-label="Confirm structure name"
                    placeholder={artifactName}
                    value={nameInput}
                    onChange={(event) => setNameInput(event.target.value)}
                    status={nameInput && !nameMatches ? 'error' : undefined}
                />
                {requiresPhrase && (
                    <Input
                        aria-label="Confirm deletion phrase"
                        placeholder={CONFIRM_PHRASE}
                        value={phraseInput}
                        onChange={(event) => setPhraseInput(event.target.value)}
                        status={phraseInput && !phraseMatches ? 'error' : undefined}
                    />
                )}
            </FlexCol>
        </Modal>
    );
};

export default DeleteStructureConfirm;
